import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { persistStorage } from '@/tutorial/storage';

/**
 * Tema do CRM (spec `crm-alvo-v2`): claro, escuro ou "seguir o sistema".
 *
 * `mode` é a ESCOLHA da pessoa (é o que persiste); `isDark` é o tema
 * RESOLVIDO — `system` vira claro/escuro pela `prefers-color-scheme` do
 * aparelho e acompanha a troca ao vivo. Quem só pinta o ícone de sol/lua lê
 * `isDark`; quem oferece o seletor de três opções lê `mode`.
 *
 * A classe `.dark` vai no `<html>` (é onde `src/index.css` redefine os tokens),
 * junto com `color-scheme`, para controles nativos e barra de rolagem
 * acompanharem.
 */
export type ThemeMode = 'light' | 'dark' | 'system';

export const THEME_STORAGE_KEY = 'opruscrm-theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

interface ThemeState {
  readonly mode: ThemeMode;
  readonly isDark: boolean;
  /** Alterna claro↔escuro a partir do que está NA TELA (sai de `system`). */
  toggle: () => void;
  setMode: (mode: ThemeMode) => void;
  /** Atalho legado (tutorial): `true` = escuro, `false` = claro. */
  setTheme: (isDark: boolean) => void;
}

/** O aparelho pede escuro? Sem `matchMedia` (SSR/ambiente antigo), claro. */
export function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(DARK_QUERY).matches;
}

export function resolveIsDark(mode: ThemeMode): boolean {
  if (mode === 'system') return systemPrefersDark();
  return mode === 'dark';
}

/** Pinta o documento: classe `.dark` + `color-scheme` no `<html>`. */
export function applyThemeToDocument(isDark: boolean): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.toggle('dark', isDark);
  root.style.colorScheme = isDark ? 'dark' : 'light';
}

function isThemeMode(value: unknown): value is ThemeMode {
  return value === 'light' || value === 'dark' || value === 'system';
}

/**
 * Lê o estado persistido de qualquer versão. A v0 gravava só `{ isDark }`:
 * quem tinha escolhido escuro continua no escuro — ninguém perde a escolha com
 * a troca de identidade. Sem escolha legível (nada salvo, lixo), segue o
 * sistema.
 */
export function migrateThemeState(persisted: unknown): { mode: ThemeMode } {
  const state = (persisted ?? {}) as { mode?: unknown; isDark?: unknown };
  if (isThemeMode(state.mode)) return { mode: state.mode };
  if (typeof state.isDark === 'boolean') return { mode: state.isDark ? 'dark' : 'light' };
  return { mode: 'system' };
}

function stateFor(mode: ThemeMode): Pick<ThemeState, 'mode' | 'isDark'> {
  const isDark = resolveIsDark(mode);
  applyThemeToDocument(isDark);
  return { mode, isDark };
}

export const useTheme = create<ThemeState>()(
  persist(
    (set) => ({
      mode: 'system',
      isDark: false,
      toggle: () => set((state) => stateFor(state.isDark ? 'light' : 'dark')),
      setMode: (mode) => set(() => stateFor(mode)),
      setTheme: (isDark) => set(() => stateFor(isDark ? 'dark' : 'light')),
    }),
    {
      name: THEME_STORAGE_KEY,
      version: 1,
      // Modo demonstracao (spec `tutorial-crm` 6.1-2): memoria no iframe.
      storage: createJSONStorage(persistStorage),
      // So a escolha persiste; `isDark` depende do aparelho e e' recalculado.
      partialize: (state) => ({ mode: state.mode }),
      migrate: (persisted) => migrateThemeState(persisted),
      merge: (persisted, current) => {
        const { mode } = migrateThemeState(persisted);
        return { ...current, mode, isDark: resolveIsDark(mode) };
      },
      onRehydrateStorage: () => (state) => {
        applyThemeToDocument(state?.isDark ?? systemPrefersDark());
      },
    },
  ),
);

/** Ação de alternar claro↔escuro — para quem está fora de componente. */
export function toggleTheme(): void {
  useTheme.getState().toggle();
}

/**
 * Hook do botão de sol/lua (trilho, landing, login): o tema na tela e a ação
 * de alternar. Seletor por campo — só re-renderiza quando o tema muda.
 */
export function useThemeToggle(): { readonly isDark: boolean; readonly toggle: () => void } {
  const isDark = useTheme((state) => state.isDark);
  const toggle = useTheme((state) => state.toggle);
  return { isDark, toggle };
}

let systemListenerBound = false;

function onSystemSchemeChange(event: MediaQueryListEvent): void {
  if (useTheme.getState().mode !== 'system') return;
  useTheme.setState({ isDark: event.matches });
  applyThemeToDocument(event.matches);
}

/**
 * Liga o tema ao documento na partida do app (`main.tsx`) e passa a ouvir a
 * troca de claro/escuro do aparelho — que só tem efeito em `mode: 'system'`.
 * Idempotente: chamar de novo não registra um segundo ouvinte.
 */
export function initTheme(): void {
  const isDark = resolveIsDark(useTheme.getState().mode);
  useTheme.setState({ isDark });
  applyThemeToDocument(isDark);

  if (systemListenerBound) return;
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

  window.matchMedia(DARK_QUERY).addEventListener('change', onSystemSchemeChange);
  systemListenerBound = true;
}

/** Só para teste: permite religar o ouvinte do sistema entre casos. */
export function resetThemeListenerForTests(): void {
  systemListenerBound = false;
}
